<script setup>
import { ref } from 'vue';
// Utils.
import { default as t } from '../../translations/downloads';

// Model value.
const modelValue = defineModel();

// Props.
const props = defineProps({
  modalType: {
    type: String,
    required: true,
  },
  lang: {
    type: String,
    default: '', // = Global (English)
  },
  product: {
    // for Downloads modal only
    type: Object,
    required: false,
  },
});

// Refs.
const dialogModal = ref(null);

// Methods.
const openDialogModal = () => {
  if (dialogModal.value) {
    dialogModal.value.showModal();
  }
};

const closeDialogModal = () => {
  dialogModal.value.setAttribute('closing', '');

  dialogModal.value.addEventListener(
    'animationend',
    () => {
      dialogModal.value.removeAttribute('closing');
      dialogModal.value.close();
      modelValue.value = false;
    },
    { once: true },
  );
};

// Click outside the dialog handler.
const lightDismiss = ({ target }) => {
  if (target.nodeName === 'DIALOG' || target.getAttribute('open')) {
    closeDialogModal();
  }
};

// Expose dialog element and open method.
// @todo check how legit the use of this is.
// QA: Why do we need to expose component internals? Shouldn't this be handled by v-if and v-model?
defineExpose({ openDialogModal, closeDialogModal, dialogModal });
</script>

<template>
  <dialog
    :class="[
      'dialog-modal',
      `dialog-modal--${modalType}`,
      'dialog-modal--component',
    ]"
    @click="lightDismiss"
    ref="dialogModal">
    <!-- @todo there is big potential to clean this even more -->
    <div class="dialog-modal__window">
      <!-- If modal has prop product, it's a downloads modal ... -->
      <!-- ... meaning the content has a bit different structure -->
      <template v-if="props.product && Object.keys(props.product).length">
        <!-- Header. -->
        <div class="dialog-modal__header">
          {{ t.modal.chooseLanguage }}
          <button
            class="dialog-modal__close"
            @click="closeDialogModal"></button>
        </div>

        <!-- Content. -->
        <div class="dialog-modal__content">
          <!-- @todo do we move this out into another component? -->
          <template v-if="product.msds.files && product.msds.files.length">
            <a
              v-for="(item, idx) in product.msds.files"
              :key="idx"
              :href="item.url"
              class="dialog-modal__item">
              <div>
                <div v-if="item.language" class="dialog-modal__language">
                  {{ item.language }}
                </div>
                <div v-if="item.filename" class="dialog-modal__file">
                  {{ item.filename }}
                </div>
              </div>
              <span class="dialog-modal__download"></span>
            </a>
          </template>
          <template v-else>
            <!-- @todo not sure about these classes, if needed 🤔 -->
            <div
              :class="{ 'modal__content--show': modalOpen }"
              class="dialog-modal__content-empty">
              <p v-html="t.modal.emptyText.replace('@lang/', props.lang)"></p>
            </div>
          </template>
        </div>
      </template>

      <!-- The 'normal' content structure with slots. -->
      <template v-else>
        <!-- Title slot. -->
        <div class="dialog-modal__header">
          <slot name="header"></slot>
          <button
            class="dialog-modal__close"
            @click="closeDialogModal"></button>
        </div>
        <!-- Content slot. -->
        <div class="dialog-modal__content">
          <slot name="content"></slot>
        </div>
      </template>
    </div>
  </dialog>
</template>
